/**
 * La única parte del audit log que toca Firestore.
 *
 * Está separada de `auditLog.ts` a propósito: ese módulo es pura derivación de estados y no importa
 * el SDK de cliente, así que se puede ejecutar en tests de node y en el harness que comprueba contra
 * un Firestore real que lo que el backend escribe es lo que el front lee. Aquí solo vive la query.
 *
 * La decisión de cuándo seguir leyendo hacia atrás vive en `auditLogPaginacion.ts`, por lo mismo:
 * aquí queda solo el pegamento, que es lo que no se puede probar sin emulador.
 */
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';

import { confirmacionesDeDocumentos, type Confirmacion } from './auditLog';
import {
  esCabecera,
  previewIdsSinIntent,
  unirDocs,
  type DocAudit,
} from './auditLogPaginacion';
import { db } from './firebase';

/** Cuántos eventos trae la primera página. Es el coste de CADA polling, así que se queda como estaba. */
const MAX_EVENTOS = 300;

/**
 * Páginas hacia atrás, solo cuando falta la cabecera de una confirmación. Son grandes y pocas a
 * propósito: lo que cuesta aquí son los viajes de ida y vuelta, no los documentos. 1.000 × 12 cubre
 * una confirmación de 12.000 chunks (2,4 M de viajes) sin dejar de tener un techo.
 */
const EVENTOS_POR_PAGINA = 1000;
const MAX_PAGINAS = 12;

/**
 * Cabeceras (`intent`/`outcome`) ya encontradas, por id de documento.
 *
 * Cachear es correcto porque `audit_log` es insert-only (invariante 6): un evento no cambia nunca.
 * Y es lo que hace que el rebusque sea de una sola vez — el polling de cada 10 s vuelve a leer solo
 * la primera página. Vive en el módulo, así que se vacía al recargar la página, que es cuando toca.
 */
const cabeceras = new Map<string, DocAudit>();

/** Confirmaciones cuya cabecera se buscó y no se alcanzó. No se vuelve a intentar (ver el módulo). */
const rebuscadasSinExito = new Set<string>();

const aDoc = (doc: QueryDocumentSnapshot): DocAudit => ({
  id: doc.id,
  data: doc.data() as Record<string, unknown>,
});

function recordarCabeceras(docs: readonly DocAudit[]): void {
  for (const doc of docs) {
    if (esCabecera(doc)) cabeceras.set(doc.id, doc);
  }
}

export async function leerConfirmaciones(ahoraMs: number = Date.now()): Promise<Confirmacion[]> {
  const primera = await getDocs(
    query(collection(db, 'audit_log'), orderBy('createdAt', 'desc'), limit(MAX_EVENTOS)),
  );
  const recientes = primera.docs.map(aDoc);
  recordarCabeceras(recientes);

  // Rebusque: solo si a alguna confirmación de la primera página le falta su `intent`. En el caso
  // normal —ninguna confirmación gigante en vuelo— esto no lanza ni una lectura extra.
  let cursor = primera.docs.at(-1);
  let paginas = 0;
  while (
    cursor !== undefined &&
    paginas < MAX_PAGINAS &&
    previewIdsSinIntent(unirDocs(recientes, [...cabeceras.values()]), rebuscadasSinExito).length > 0
  ) {
    const pagina = await getDocs(
      query(
        collection(db, 'audit_log'),
        orderBy('createdAt', 'desc'),
        startAfter(cursor),
        limit(EVENTOS_POR_PAGINA),
      ),
    );
    if (pagina.empty) break;

    // Solo se guardan las cabeceras: los `progress` son el 99 % del volumen y no aportan nada que la
    // primera página no tenga ya (los contadores son acumulados, así que el último manda).
    recordarCabeceras(pagina.docs.map(aDoc));
    cursor = pagina.docs.at(-1);
    paginas++;
  }

  const docs = unirDocs(recientes, [...cabeceras.values()]);
  // Lo que sigue faltando, no se vuelve a buscar. Es visible: sin `intent`, la tarjeta se pinta con
  // "(criterios no registrados)" en vez de inventarse nada.
  for (const previewId of previewIdsSinIntent(docs, rebuscadasSinExito)) {
    rebuscadasSinExito.add(previewId);
  }

  return confirmacionesDeDocumentos(docs, ahoraMs);
}
